import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  formatSeoApiError,
  listEntitiesByTier,
  patchSeoEntity,
  fetchEntityRaw,
  normalizeEntityType,
} from '../../../utils/adminSeo';

const TABS = [1, 2, 3];

export default function SeoPriorityEntityScreen({ title, entityType: entityTypeProp }) {
  const { entityType: routeType } = useParams();
  const entityType = normalizeEntityType(entityTypeProp || routeType);
  const [tier, setTier] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setErr('');
    setLoading(true);
    listEntitiesByTier(entityType, tier, { q, limit: 150 })
      .then((r) => {
        setRows(r.entities || []);
        setTotal(r.total || 0);
      })
      .catch((e) => setErr(formatSeoApiError(e)))
      .finally(() => setLoading(false));
  }, [entityType, tier, q]);

  useEffect(() => {
    load();
  }, [load]);

  const changeTier = async (ent, newTier) => {
    try {
      await patchSeoEntity(entityType, ent.id, { tier: newTier, manual_override: true });
      toast.success(`Tier set to ${newTier}`);
      load();
    } catch (e) {
      toast.error(formatSeoApiError(e));
    }
  };

  const viewRaw = async (ent) => {
    try {
      const data = await fetchEntityRaw(entityType, ent.id);
      setRaw(data);
    } catch (e) {
      toast.error(formatSeoApiError(e));
    }
  };

  const label = title || `${entityType} entities`;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-[#1B375B]">{label}</h2>
      <div className="flex gap-2">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={`px-3 py-1 rounded-lg text-sm ${
              tier === t ? 'bg-[#1B375B] text-white' : 'border hover:bg-gray-50'
            }`}
            onClick={() => setTier(t)}
          >
            Tier {t}
          </button>
        ))}
      </div>
      <input
        className="border rounded px-3 py-1 text-sm w-full max-w-md"
        placeholder="Search…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {err && <p className="text-red-600 text-sm">{err}</p>}
      {!err && !loading && total === 0 && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
          No entities in Tier {tier}. Run Import &amp; Sync or{' '}
          <code className="text-xs">python -m scripts.sync_tier_manifests --apply</code> to backfill tiers.
        </p>
      )}
      <p className="text-xs text-gray-500">
        {total} in tier {tier}
        {loading ? ' · loading…' : ''}
      </p>
      <div className="bg-white border rounded-lg overflow-x-auto max-h-[60vh]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="p-2 text-left">Name</th>
              <th className="p-2">Priority</th>
              <th className="p-2">Status</th>
              <th className="p-2">Page</th>
              <th className="p-2">Health</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-2">
                  {r.name || r.name_display || r.slug || `${r.origin_iata}-${r.dest_iata}` || r.iata}
                </td>
                <td className="p-2 text-center">{r.entity_priority ?? r.tier ?? '—'}</td>
                <td className="p-2 text-center">{r.status}</td>
                <td className="p-2 text-center text-xs">
                  {r.page_publish_status || '—'}
                  {r.page_indexable === false ? ' · noindex' : ''}
                </td>
                <td className="p-2 text-center">{r.page_health_score ?? '—'}</td>
                <td className="p-2 space-x-1">
                  <button type="button" className="text-xs underline text-[#659A9B]" onClick={() => viewRaw(r)}>
                    Raw
                  </button>
                  {tier > 1 && (
                    <button type="button" className="text-xs underline" onClick={() => changeTier(r, tier - 1)}>
                      ↑
                    </button>
                  )}
                  {tier < 3 && (
                    <button type="button" className="text-xs underline" onClick={() => changeTier(r, tier + 1)}>
                      ↓
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !err && rows.length === 0 && (
          <p className="p-4 text-gray-500 text-sm text-center">No rows to display.</p>
        )}
      </div>
      {raw && (
        <div className="border rounded-lg p-3 bg-gray-50">
          <div className="flex justify-between mb-2">
            <strong className="text-sm">Raw / linked data</strong>
            <button type="button" className="text-xs" onClick={() => setRaw(null)}>
              Close
            </button>
          </div>
          <pre className="text-xs overflow-auto max-h-64">{JSON.stringify(raw, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
